import { TZDateMini } from '@date-fns/tz';
import { padStart } from 'lodash';
import type { FC } from 'react';
import { useEffect, useId, useMemo, useState } from 'react';
import {
  getTimeSelectorPlaceholder,
  isTimeSelectorDisabled,
  type InputTime,
} from 'src/components/form/datepicker/utils';
import { FieldLayout } from 'src/components/form/field-layout';
import { FormModeProvider } from 'src/components/form/form-mode-context';
import { FieldLabel } from 'src/components/form/label';
import { SimpleSelect } from 'src/components/form/select';
import { Hint } from 'src/components/text/hint';
import { timeZone } from 'src/utils/date-time-format';

export function useTimeValue(value: Date | null) {
  const derivedTimeValue = useMemo(() => {
    const valueInBerlin = value && new TZDateMini(value, timeZone);

    const hours = valueInBerlin
      ? padStart(String(valueInBerlin.getHours()), 2, '0')
      : '';
    const minutes = valueInBerlin
      ? padStart(String(valueInBerlin.getMinutes()), 2, '0')
      : '';
    const timeString = valueInBerlin ? `${hours}:${minutes}` : '';

    return { hours, minutes, timeString };
  }, [value]);

  const [timeValue, setTimeValue] = useState(derivedTimeValue);

  // Sync time when value is updated from outside
  useEffect(() => setTimeValue(derivedTimeValue), [derivedTimeValue]);

  const state = useMemo(
    () => ({ value: timeValue, setValue: setTimeValue }),
    [timeValue]
  );

  return state;
}

type TimeSelectorProps = {
  /** The current time as "HH:mm". */
  timeValue: string;
  onTimeChange: (timeValue: string) => void;
  time: Exclude<InputTime, false>;
  disabled?: boolean;
  namePrefix?: 'start' | 'end';
};

export const TimeSelector: FC<TimeSelectorProps> = ({
  timeValue,
  onTimeChange,
  time,
  disabled,
  namePrefix,
}) => {
  const prefix = namePrefix ? `${namePrefix}-` : '';

  const enhancedOptions = useMemo(
    () =>
      // add current time options, if needed
      (timeValue && !timeOptions.includes(timeValue)
        ? [timeValue, ...timeOptions]
        : timeOptions
      ).map((time) => ({ label: time, value: time })),
    [timeValue]
  );

  const fieldId = useId();

  const field = (
    <SimpleSelect
      name={`${prefix}time`}
      placeholder={getTimeSelectorPlaceholder(time)}
      value={timeValue}
      options={enhancedOptions}
      onChange={onTimeChange}
      disabled={disabled || isTimeSelectorDisabled(time)}
      renderOptions={({ children }) => (
        <>
          {children}
          <div className="sticky -bottom-md -mx-xs -mb-md bg-white px-xl py-sm">
            <Hint size="sm">All times in CE(S)T</Hint>
          </div>
        </>
      )}
    />
  );

  const label = fieldLabels[namePrefix ?? 'default'];

  return (
    <FormModeProvider mode="regular">
      <div className="w-[10.5rem]">
        {label ? (
          <FieldLayout stacked>
            <FieldLabel
              name={`${prefix}TimeLabel`}
              displayLabel={label}
              fieldId={fieldId}
            />
            {field}
          </FieldLayout>
        ) : (
          field
        )}
      </div>
    </FormModeProvider>
  );
};

const fieldLabels: Record<string, string | null> = {
  start: 'Start Time',
  end: 'End Time',
  default: null,
};

const timeOptions = [
  '00:00',
  '01:00',
  '02:00',
  '03:00',
  '04:00',
  '05:00',
  '06:00',
  '07:00',
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
  '22:00',
  '23:00',
];
